/**
 * BakeOffLeague: the team's fantasy league for this season of The Great
 * British Baking Show, at /t/:tenantSlug/bake-off. Behind
 * tenants.config.features.bake_off_league; while that is off, or when the
 * server answers 404 (a seat outside the staff), the page is Olive's
 * not-found page.
 *
 * Order (Amanda's brief): how it works; your team for the season; this
 * week's guesses; the leaderboard; past weeks with everyone's guesses;
 * the episodes; the scoring; and last, on purpose, the owner box "Post
 * what happened on the show" (on the prototype, results were posted by
 * someone who believed she was saving her own guesses).
 *
 * The server decides what anyone may see and computes every score; the
 * page shows what it is given. A failed save keeps every selection.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/primitives/Button';
import { Field } from '@/components/primitives/Field';
import { PageShell } from '@/components/primitives/PageShell';
import { SelectField } from '@/components/primitives/SelectField';
import { NotFoundPage } from '@/components/NotFoundPage';
import { useTenant } from '@/lib/auth';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { useSupportContact } from '@/hooks/useSupportContact';
import { bakeOffLeagueOn } from '@/lib/tenantConfig';
import { canRunBakeOff } from '@/lib/capabilities';
import { errorCodeOf, pageWriteText } from '@/lib/errorCopy';
import { proseDate } from '@/lib/proseDate';
import { sharedPlaces } from '@/lib/bakeOffPlaces';
import {
  bakeOffQueryKey,
  bakerName,
  bakersInFor,
  putBakeOff,
  useBakeOff,
  type BakeOffBaker,
  type BakeOffView,
} from '@/lib/bakeOff';

const DRAFT_CLOSED = 'The draft has closed, so teams are set for the season. You can pick again next season.';
const WEEK_CLOSED = "That week is closed for guesses. Make your guesses on this week's games.";

const BUNTING = ['bg-warm-400', 'bg-sage-200', 'bg-terracotta-soft', 'bg-sand', 'bg-sage-300'];

type Tint = 'blush' | 'lavender' | 'mist' | 'butter' | 'mint' | 'paper';

const TINT: Record<Tint, string> = {
  blush: 'border-terracotta-soft bg-pepper-soft',
  lavender: 'border-heather-deep/30 bg-heather',
  mist: 'border-mist-deep/30 bg-mist',
  butter: 'border-warm-200 bg-warm-100',
  mint: 'border-sage-200 bg-sage-50',
  paper: 'border-sage-200 bg-paper',
};

const BAKER_POINTS: ReadonlyArray<{ emoji: string; points: string; what: string }> = [
  { emoji: '🎉', points: '+1', what: 'Survives the week' },
  { emoji: '⭐', points: '+5', what: 'Star Baker' },
  { emoji: '🏅', points: '+3', what: 'Wins the technical' },
  { emoji: '🤝', points: '+2', what: 'Hollywood handshake' },
  { emoji: '👋', points: '0', what: 'Goes home: nothing that week, or after' },
];

const GUESS_POINTS: ReadonlyArray<{ emoji: string; points: string; what: string }> = [
  { emoji: '⭐', points: '+3', what: 'You called Star Baker' },
  { emoji: '🏅', points: '+3', what: 'You called the technical' },
  { emoji: '👋', points: '+3', what: 'You called who went home' },
];

const GUESS_FIELDS = [
  { key: 'star_baker', label: 'Star Baker', emoji: '⭐', tint: 'butter' },
  { key: 'technical_winner', label: 'Technical winner', emoji: '🏅', tint: 'mist' },
  { key: 'going_home', label: 'Going home', emoji: '👋', tint: 'blush' },
] as const;

const MEDALS = ['🥇', '🥈', '🥉'];

function PointsTile({ emoji, points, what }: { emoji: string; points: string; what: string }): ReactNode {
  return (
    <li className="flex items-center gap-3 rounded-card bg-paper/80 px-3 py-2">
      <span aria-hidden="true" className="text-2xl">
        {emoji}
      </span>
      <span className="min-w-[2.5rem] font-display text-2xl text-sage-800 tabular-nums">{points}</span>
      <span className="text-sm text-ink">{what}</span>
    </li>
  );
}

function Bunting(): ReactNode {
  return (
    <div aria-hidden="true" className="flex w-full overflow-hidden" data-testid="bake-off-bunting">
      {Array.from({ length: 24 }, (_, i) => (
        <span
          key={i}
          className={`h-5 min-w-0 flex-1 ${BUNTING[i % BUNTING.length]}`}
          style={{ clipPath: 'polygon(0 0, 100% 0, 50% 100%)' }}
        />
      ))}
    </div>
  );
}

function Card({
  title,
  emoji,
  tint = 'paper',
  children,
  testId,
  owner = false,
}: {
  title: string;
  emoji: string;
  tint?: Tint;
  children: ReactNode;
  testId?: string;
  owner?: boolean;
}) {
  return (
    <section
      data-bake-off-section=""
      data-testid={testId}
      className={`space-y-3 rounded-card border-2 p-4 shadow-sh1 sm:p-5 ${owner ? 'border-dashed border-warm-400 bg-sand' : TINT[tint]}`}
    >
      <h2 className="flex items-center gap-2 font-display text-xl text-sage-800">
        <span aria-hidden="true">{emoji}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

/** A save's error: the page's own sentence for the two refusals it can
 *  explain, the house write sentence for anything else. */
function saveError(e: unknown, contact: ReturnType<typeof useSupportContact>): string {
  const code = errorCodeOf(e);
  if (code === 'draft_closed') return DRAFT_CLOSED;
  if (code === 'week_closed') return WEEK_CLOSED;
  return pageWriteText(e, contact);
}

function ErrorLine({ text }: { text: string | null }) {
  if (text === null) return null;
  return (
    <p role="alert" className="text-sm text-pepper">
      {text}
    </p>
  );
}

/** What is on file, so a save visibly lands: "Saved" while the picks on
 *  screen match it, "Not saved yet" once they differ, nothing before the
 *  first save. */
function SavedNote({ testId, saved, savedText, unsavedText }: { testId: string; saved: boolean | null; savedText: string; unsavedText: string }) {
  if (saved === null) return null;
  return (
    <p
      role="status"
      data-testid={testId}
      className={saved ? 'inline-block rounded-full bg-sage-100 px-3 py-1 text-sm font-semibold text-sage-800' : 'text-sm text-ink-soft'}
    >
      {saved ? savedText : unsavedText}
    </p>
  );
}

function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

export function BakeOffLeague(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const { data: tenantConfig, isLoading: configLoading } = useTenantConfig(tenantSlug);
  const on = bakeOffLeagueOn(tenantConfig);
  const query = useBakeOff(tenantSlug, { enabled: on && whoami !== null });
  const queryClient = useQueryClient();
  const contact = useSupportContact();
  const view = query.data ?? null;

  // Every selection lives here, so a failed save keeps it on screen.
  const [team, setTeam] = useState<string[]>([]);
  const [guess, setGuess] = useState({ star_baker: '', technical_winner: '', going_home: '' });
  const [resultsWeek, setResultsWeek] = useState<number | null>(null);
  const [results, setResults] = useState({ star_baker: '', technical_winner: '', handshakes: [] as string[], eliminated: [] as string[] });
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (seeded || view === null) return;
    setSeeded(true);
    setTeam(view.me.team?.bakers ?? []);
    const open = view.open_week;
    const mine = open !== null ? view.me.guesses[String(open)] : undefined;
    if (mine) setGuess(mine);
    setResultsWeek(open ?? view.season?.weeks[0]?.number ?? null);
  }, [seeded, view]);

  const onSaved = (_v: BakeOffView, vars: { slug: string }) => queryClient.setQueryData(bakeOffQueryKey(vars.slug), _v);
  const teamMut = useMutation({
    mutationFn: (vars: { slug: string; bakers: string[] }) => putBakeOff(vars.slug, '/my-team', { bakers: vars.bakers }),
    onSuccess: onSaved,
  });
  const guessMut = useMutation({
    mutationFn: (vars: { slug: string; week: number; guess: Record<string, string> }) =>
      putBakeOff(vars.slug, `/my-guesses/${vars.week}`, vars.guess),
    onSuccess: onSaved,
  });
  const resultsMut = useMutation({
    mutationFn: (vars: { slug: string; week: number; body: Record<string, unknown> }) =>
      putBakeOff(vars.slug, `/results/${vars.week}`, vars.body),
    onSuccess: onSaved,
  });
  const draftMut = useMutation({
    mutationFn: (vars: { slug: string; closed: boolean }) => putBakeOff(vars.slug, '/draft', { closed: vars.closed }),
    onSuccess: onSaved,
  });

  // After every hook: the gates.
  if (!configLoading && tenantConfig !== undefined && !on) return <NotFoundPage />;
  if (query.isError && errorCodeOf(query.error) === 'not_found') return <NotFoundPage />;

  const season = view?.season ?? null;
  const owner = canRunBakeOff(whoami);
  const openWeek = view?.open_week ?? null;
  const openWeekInfo = season?.weeks.find((w) => w.number === openWeek) ?? null;
  const inThisWeek: BakeOffBaker[] = view && openWeek !== null ? bakersInFor(view, openWeek) : [];
  const savedTeam = view?.me.team?.bakers ?? [];
  const savedTeamNames = season ? savedTeam.map((id) => bakerName(season, id)).join(' and ') : '';
  const teamSaved: boolean | null = savedTeam.length === 0 ? null : sameIds(team, savedTeam);
  const savedGuess = view && openWeek !== null ? view.me.guesses[String(openWeek)] : undefined;
  const guessSaved: boolean | null = !savedGuess
    ? null
    : savedGuess.star_baker === guess.star_baker && savedGuess.technical_winner === guess.technical_winner && savedGuess.going_home === guess.going_home;
  const resultsChoices: BakeOffBaker[] = view && resultsWeek !== null ? bakersInFor(view, resultsWeek) : [];
  const places = sharedPlaces(view ? view.players.map((p) => p.total) : []);
  const postedWeeks = view ? Object.keys(view.results).map(Number).sort((a, b) => a - b) : [];
  const eliminatedBy = (id: string): number | null => {
    for (const w of postedWeeks) if (view!.results[String(w)]!.eliminated.includes(id)) return w;
    return null;
  };

  const toggle = (list: string[], id: string, max?: number): string[] =>
    list.includes(id) ? list.filter((x) => x !== id) : max !== undefined && list.length >= max ? list : [...list, id];

  return (
    <PageShell
      title="🧁 Bake Off League 🧁"
      subtitle={season ? season.name : 'The Great British Baking Show, played by the team'}
      async={{ what: 'the league', isLoading: configLoading || query.isLoading, isError: query.isError, error: query.error, onRetry: query.refetch }}
      testId="bake-off-page"
    >
      <div className="space-y-5" data-testid="bake-off-page-body">
        <Bunting />
        {season === null ? (
          <p className="text-base text-ink-soft">The season hasn't been set up yet. Check back soon.</p>
        ) : (
          <>
            <Card title="How it works" emoji="🧁" tint="blush">
              <ol className="grid gap-3 sm:grid-cols-2">
                {[
                  ['1', 'Pick your team', 'Two bakers for the whole season. They earn you points every week they stay in the tent.'],
                  ['2', 'Guess each week', 'Star Baker, the technical winner and who goes home, before Friday’s episode drops.'],
                ].map(([n, head, body]) => (
                  <li key={n} className="flex gap-3 rounded-card bg-paper/80 p-3">
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-terracotta-soft font-display text-lg text-sage-800">
                      {n}
                    </span>
                    <span className="space-y-0.5">
                      <span className="block font-semibold text-ink">{head}</span>
                      <span className="block text-sm text-ink-soft">{body}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </Card>

            <Card title="Your team for the season" emoji="👩‍🍳" tint="butter" testId="bake-off-team">
              {season.draft_closed ? (
                <p className="text-sm text-ink-soft">{DRAFT_CLOSED}</p>
              ) : (
                <p className="text-sm text-ink-soft">Tap two bakers. You can change them until the draft closes.</p>
              )}
              <div className="grid grid-cols-2 gap-2 sm:max-w-md" aria-hidden="true">
                {[0, 1].map((i) => (
                  <div
                    key={i}
                    className={`rounded-card border-2 px-3 py-2 text-center ${team[i] ? 'border-sage-300 bg-paper font-semibold text-sage-800' : 'border-dashed border-warm-200 text-ink-soft'}`}
                  >
                    {team[i] ? `🍰 ${bakerName(season, team[i])}` : 'Pick a baker'}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {season.bakers.map((b) => {
                  const picked = team.includes(b.id);
                  return (
                    <Button
                      key={b.id}
                      size="sm"
                      variant={picked ? 'primary' : 'secondary'}
                      aria-pressed={picked}
                      disabled={season.draft_closed}
                      data-testid={`bake-off-team-${b.id}`}
                      onClick={() => setTeam((t) => toggle(t, b.id, 2))}
                    >
                      {b.name}
                    </Button>
                  );
                })}
              </div>
              {!season.draft_closed && (
                <Button
                  data-testid="bake-off-team-save"
                  loading={teamMut.isPending}
                  disabled={team.length !== 2}
                  onClick={() => teamMut.mutate({ slug: tenantSlug, bakers: team })}
                >
                  Save my team
                </Button>
              )}
              <SavedNote
                testId="bake-off-team-saved"
                saved={teamSaved}
                savedText={`✨ Saved! ${savedTeamNames} are your bakers.`}
                unsavedText={`Not saved yet. Your saved team is ${savedTeamNames}.`}
              />
              <ErrorLine text={teamMut.isError ? saveError(teamMut.error, contact) : null} />
            </Card>

            <Card title="This week's guesses" emoji="🔮" tint="lavender" testId="bake-off-guesses">
              {openWeekInfo === null ? (
                <p className="text-sm text-ink-soft">The season is over. Thanks for playing.</p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-lg text-ink">{`Week ${openWeekInfo.number}${openWeekInfo.theme ? ` · ${openWeekInfo.theme}` : ''}`}</span>
                    <span className="rounded-full bg-paper px-3 py-0.5 text-sm text-heather-deep">
                      <span aria-hidden="true">📺 </span>
                      {`On Netflix ${proseDate(openWeekInfo.release_date)}`}
                    </span>
                  </div>
                  <p className="text-sm text-ink-soft">Get your guesses in before the episode drops.</p>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {GUESS_FIELDS.map(({ key, label, emoji, tint }) => (
                      <div key={key} className={`space-y-1 rounded-card border p-3 ${TINT[tint]}`}>
                      <span aria-hidden="true" className="block text-2xl">
                        {emoji}
                      </span>
                      <Field label={label} htmlFor={`bake-off-guess-${key}`}>
                        <SelectField
                          size="field"
                          id={`bake-off-guess-${key}`}
                          data-testid={`bake-off-guess-${key}`}
                          value={guess[key]}
                          onChange={(e) => setGuess((g) => ({ ...g, [key]: e.target.value }))}
                        >
                          <option value="">Pick a baker</option>
                          {inThisWeek.map((b) => (
                            <option key={b.id} value={b.id}>
                              {b.name}
                            </option>
                          ))}
                        </SelectField>
                      </Field>
                      </div>
                    ))}
                  </div>
                  <Button
                    data-testid="bake-off-guesses-save"
                    loading={guessMut.isPending}
                    disabled={!guess.star_baker || !guess.technical_winner || !guess.going_home}
                    onClick={() => guessMut.mutate({ slug: tenantSlug, week: openWeekInfo.number, guess })}
                  >
                    Save my guesses
                  </Button>
                  <SavedNote
                    testId="bake-off-guesses-saved"
                    saved={guessSaved}
                    savedText={`✨ Saved! Your Week ${openWeekInfo.number} guesses are in.`}
                    unsavedText={`Not saved yet. Your saved Week ${openWeekInfo.number} guesses still stand.`}
                  />
                  <ErrorLine text={guessMut.isError ? saveError(guessMut.error, contact) : null} />
                </>
              )}
            </Card>

            <Card title="Leaderboard" emoji="🏆" tint="mint" testId="bake-off-leaderboard">
              {view!.players.length === 0 ? (
                <p className="text-sm text-ink-soft">Be the first to pick a team.</p>
              ) : (
                <div className="max-w-full overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-ink-soft">
                        <th className="py-1 pr-3 font-semibold">#</th>
                        <th className="py-1 pr-3 font-semibold">Player</th>
                        {postedWeeks.map((w) => (
                          <th key={w} className="py-1 pr-3 font-semibold tabular-nums">{`Wk ${w}`}</th>
                        ))}
                        <th className="py-1 font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {view!.players.map((p, i) => (
                        <tr key={p.user_id} className="border-t border-sage-200 align-top">
                          <td className="py-1.5 pr-3 tabular-nums" data-testid="bake-off-place">
                            {places[i]! <= MEDALS.length ? (
                              <span role="img" aria-label={`Place ${places[i]}`} className="text-lg">
                                {MEDALS[places[i]! - 1]}
                              </span>
                            ) : (
                              places[i]
                            )}
                          </td>
                          <td className="py-1.5 pr-3">
                            <span className="font-semibold text-ink">{p.name}</span>
                            {p.team && (
                              <span className="block text-ink-soft">
                                {p.team.bakers
                                  .map((id) => `${bakerName(season, id)}${eliminatedBy(id) !== null ? ' (out)' : ''}`)
                                  .join(' · ')}
                              </span>
                            )}
                          </td>
                          {postedWeeks.map((w) => (
                            <td key={w} className="py-1.5 pr-3 tabular-nums">
                              {p.weeks[String(w)] ?? 0}
                            </td>
                          ))}
                          <td className="py-1.5 font-semibold tabular-nums">{p.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {postedWeeks.length > 0 && (
              <Card title="Past weeks" emoji="📖" tint="paper" testId="bake-off-past">
                {[...postedWeeks].reverse().map((w) => {
                  const r = view!.results[String(w)]!;
                  return (
                    <div key={w} className="space-y-1 border-t border-sage-200 pt-2 first:border-t-0 first:pt-0">
                      <p className="text-base font-semibold text-ink">{`Week ${w}`}</p>
                      <p className="text-sm text-ink">
                        {`Star Baker: ${bakerName(season, r.star_baker) || 'none'} · Technical: ${bakerName(season, r.technical_winner) || 'none'} · Handshakes: ${r.handshakes.map((id) => bakerName(season, id)).join(', ') || 'none'} · Went home: ${r.eliminated.map((id) => bakerName(season, id)).join(', ') || 'no one'}`}
                      </p>
                      <ul className="space-y-0.5 text-sm text-ink-soft">
                        {view!.players
                          .filter((p) => p.guesses?.[String(w)])
                          .map((p) => {
                            const g = p.guesses![String(w)]!;
                            return (
                              <li key={p.user_id}>
                                {`${p.name}: ${bakerName(season, g.star_baker)} · ${bakerName(season, g.technical_winner)} · ${bakerName(season, g.going_home)}`}
                              </li>
                            );
                          })}
                      </ul>
                    </div>
                  );
                })}
              </Card>
            )}

            <Card title="The episodes" emoji="📺" tint="mist">
              <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {season.weeks.map((w) => {
                  const done = Boolean(view!.results[String(w.number)]);
                  const now = w.number === openWeek;
                  return (
                    <li
                      key={w.number}
                      className={`space-y-0.5 rounded-card border-2 p-3 ${now ? 'border-heather-deep bg-paper' : done ? 'border-transparent bg-paper/60 text-ink-soft' : 'border-transparent bg-paper/80'}`}
                    >
                      <span className="block font-display text-lg text-sage-800">{`Week ${w.number}`}</span>
                      <span className="block text-sm font-semibold text-ink">{w.theme || 'Theme to come'}</span>
                      <span className="block text-sm text-ink-soft">{proseDate(w.release_date)}</span>
                      {now && <span className="inline-block rounded-full bg-heather px-2 text-sm text-heather-deep">This week</span>}
                      {done && <span className="inline-block text-sm text-affirm-deep">✅ Results in</span>}
                    </li>
                  );
                })}
              </ul>
            </Card>

            <Card title="Scoring" emoji="⭐" tint="butter">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-ink">Each of your bakers, every week</h3>
                  <ul className="space-y-2">
                    {BAKER_POINTS.map((p) => (
                      <PointsTile key={p.what} {...p} />
                    ))}
                  </ul>
                </div>
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-ink">Your guesses, every week</h3>
                  <ul className="space-y-2">
                    {GUESS_POINTS.map((p) => (
                      <PointsTile key={p.what} {...p} />
                    ))}
                  </ul>
                </div>
              </div>
            </Card>

            {owner && (
              <Card title="Post what happened on the show" emoji="🎬" testId="bake-off-owner-box" owner>
                <p className="text-sm text-ink">
                  This box is for the show's results, not your own guesses. Only you see it.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    variant="secondary"
                    data-testid="bake-off-draft-toggle"
                    loading={draftMut.isPending}
                    onClick={() => draftMut.mutate({ slug: tenantSlug, closed: !season.draft_closed })}
                  >
                    {season.draft_closed ? 'Reopen the draft' : 'Close the draft'}
                  </Button>
                  <span className="text-sm text-ink-soft">{season.draft_closed ? 'The draft is closed.' : 'The draft is open.'}</span>
                </div>
                <ErrorLine text={draftMut.isError ? saveError(draftMut.error, contact) : null} />
                <Field label="Week" htmlFor="bake-off-results-week">
                  <SelectField
                    size="field"
                    id="bake-off-results-week"
                    data-testid="bake-off-results-week"
                    value={resultsWeek ?? ''}
                    onChange={(e) => setResultsWeek(Number(e.target.value))}
                  >
                    {season.weeks.map((w) => (
                      <option key={w.number} value={w.number}>{`Week ${w.number}${w.theme ? ` · ${w.theme}` : ''}`}</option>
                    ))}
                  </SelectField>
                </Field>
                <div className="grid gap-3 sm:grid-cols-2">
                  {(
                    [
                      ['star_baker', 'Star Baker'],
                      ['technical_winner', 'Technical winner'],
                    ] as const
                  ).map(([key, label]) => (
                    <Field key={key} label={label} htmlFor={`bake-off-results-${key}`}>
                      <SelectField
                        size="field"
                        id={`bake-off-results-${key}`}
                        value={results[key]}
                        onChange={(e) => setResults((r) => ({ ...r, [key]: e.target.value }))}
                      >
                        <option value="">None</option>
                        {resultsChoices.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                      </SelectField>
                    </Field>
                  ))}
                </div>
                {(
                  [
                    ['handshakes', 'Hollywood handshakes'],
                    ['eliminated', 'Went home'],
                  ] as const
                ).map(([key, label]) => (
                  <div key={key} className="space-y-1">
                    <p className="text-sm font-semibold text-ink">{label}</p>
                    <div className="flex flex-wrap gap-2">
                      {resultsChoices.map((b) => {
                        const on = results[key].includes(b.id);
                        return (
                          <Button
                            key={b.id}
                            size="sm"
                            variant={on ? 'primary' : 'secondary'}
                            aria-pressed={on}
                            onClick={() => setResults((r) => ({ ...r, [key]: toggle(r[key], b.id) }))}
                          >
                            {b.name}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <Button
                  data-testid="bake-off-results-save"
                  loading={resultsMut.isPending}
                  disabled={resultsWeek === null}
                  onClick={() =>
                    resultsMut.mutate({
                      slug: tenantSlug,
                      week: resultsWeek!,
                      body: {
                        star_baker: results.star_baker || null,
                        technical_winner: results.technical_winner || null,
                        handshakes: results.handshakes,
                        eliminated: results.eliminated,
                      },
                    })
                  }
                >
                  Post this week's results
                </Button>
                <ErrorLine text={resultsMut.isError ? saveError(resultsMut.error, contact) : null} />
              </Card>
            )}
          </>
        )}
      </div>
    </PageShell>
  );
}
